import { useAuth0 } from '@auth0/auth0-react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'styles/slick-theme-overrides.css';
import PropTypes from 'prop-types';
import { useHistory, useLocation } from 'react-router-dom';

/* i18n */
import { useIntl } from 'react-intl';

/* Redux */
import { useDispatch } from 'react-redux';
import { setLoading } from 'app/slices/appSlice';

/* Styles */
import useMediaQuery from '@mui/material/useMediaQuery';

/* Material UI and other UI Dependencies */
import { Container, Box, Button, Typography, Card, CardMedia, Stack, useTheme, lighten, Alert } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import JobsCard from 'components/JobsCard';

/* Analytics */
import { trackEvent } from 'lib/analytics';

/* Assets */
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import RouteIcon from '@mui/icons-material/Route';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LooksOneTwoToneIcon from '@mui/icons-material/LooksOneTwoTone';
import LooksTwoTwoToneIcon from '@mui/icons-material/LooksTwoTwoTone';
import Looks3TwoToneIcon from '@mui/icons-material/Looks3TwoTone';
import Looks4TwoToneIcon from '@mui/icons-material/Looks4TwoTone';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SupportIcon from '@mui/icons-material/Support';
import LaunchIcon from '@mui/icons-material/Launch';

const environment = process.env.REACT_APP_ENVIRONMENT;

function LaunchHP() {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { loginWithRedirect } = useAuth0();
  const isSmallScreen = useMediaQuery('(max-width:959px)');
  const { palette } = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const handleLogin = () => {
    dispatch(setLoading(true));
    trackEvent(`LOGIN_AUTH0`);
    loginWithRedirect({
      ...(environment === 'prod' && { connection: 'Login-gov' }),
      ...(environment === 'uat' && { connection: 'Login-Gov-Test' }),
      prompt: 'login',
    });
  };

  const handleCloseInactivityAlert = () => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.delete('logged_out');
    // Update the URL without the `logged_out` parameter
    history.replace({
      search: queryParams.toString(),
    });
  };

  const getJobCard = (item) => (
    <JobsCard
      key={item.id}
      item={item}
      rating={item.rating === 1 ? 'liked' : item.rating === -1 ? 'disliked' : null}
      onButtonClick={() => {}}
      onLikeClick={() => {}}
      onDislikeClick={() => {}}
      homePage
    />
  );

  return (
    <>
      <Box
        sx={{
          bgcolor: lighten(palette.info.main, 0.9),
          color: 'cssVars.black',
          h1: {
            position: 'relative',
            display: 'inline',
            fontSize: 36,
          },
        }}
      >
        {queryParams.has('logged_out') && (
          <Container maxWidth="lg" sx={{ pt: 3 }}>
            <Alert
              sx={{
                '&& svg': {
                  fontSize: '1.5rem',
                },
              }}
              slotProps={{ closeButton: { sx: { fontSize: '1.5rem' } } }}
              severity="error"
              onClose={handleCloseInactivityAlert}
            >
              <Typography variant="h6" component="p">
                {intl.formatMessage({ id: 'homepage.inactivityAlert.title' })}
              </Typography>
            </Alert>
          </Container>
        )}

        <Container maxWidth="lg" sx={{ pt: 7, pb: { xs: 8, md: 6 } }}>
          <Box sx={{ width: '100%', fontWeight: 500, fontSize: 30 }}>
            <Grid container spacing={1} justifyContent="space-between">
              <Grid xs={12} md={6}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="h1" sx={{ fontSize: 36, mb: 2 }} color="secondary">
                    {intl.formatMessage({ id: 'homepage.hero.title' })}
                  </Typography>

                  <Typography sx={{ fontSize: 30, lineHeight: '36px', mb: 4, color: 'secondary.main' }}>
                    {intl.formatMessage(
                      { id: 'homepage.hero.discover' },
                      {
                        yourSkills: (
                          <Typography
                            variant="inherit"
                            component="span"
                            sx={{ color: 'secondary.main', display: 'inline' }}
                          >
                            {intl.formatMessage({ id: 'homepage.hero.yourSkills' })}
                          </Typography>
                        ),
                      }
                    )}
                  </Typography>

                  <Box>
                    <Button
                      variant="contained"
                      color="info"
                      sx={{
                        fontSize: 20,
                        padding: '8px 24px',
                      }}
                      onClick={handleLogin}
                      id="where-skills-take-you-public-button"
                    >
                      {intl.formatMessage({ id: 'homepage.hero.cta' })}
                    </Button>
                  </Box>
                </Box>
              </Grid>
              <Grid xs={0} md={4}>
                <picture>
                  {/* WebP format for modern browsers */}
                  <source srcSet="/images/hp-hero-image.webp" type="image/webp" />
                  {/* PNG fallback for browsers that don't support WebP */}
                  <source srcSet="/images/hp-hero-image.png" type="image/png" />

                  <img
                    src="/images/hp-hero-image.png"
                    alt="Image of young woman and man looking at a computer screen. Image is in the shape of Arkansas."
                    loading="eager"
                    width={400}
                    height={400}
                    style={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'cover',
                    }}
                  />
                </picture>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
      {/* Companies' Logos */}
      <Box>
        <Container maxWidth="lg" sx={{ pt: 4, pb: 6, '& button:before, & button': { display: 'none' } }}>
          <Typography variant="h4" component="h2" sx={{ textAlign: 'center', pb: 4, pt: 2 }}>
            {intl.formatMessage({ id: 'homepage.companies' })}
          </Typography>
          {isSmallScreen ? (
            <Box sx={{ maxWidth: '100%', overflow: 'hidden' }}>
              <Slider slidesToShow={3} slidesToScroll={1} infinite centerMode autoplay pauseOnHover>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 160 }}>
                    <img
                      src="/images/tyson-foods-logo.svg"
                      style={{ width: '70%' }}
                      alt={intl.formatMessage({ id: 'homepage.imgAlt.tysonFoods' })}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 160 }}>
                    <img
                      src="/images/accenture-logo.png"
                      style={{ width: '70%' }}
                      alt={intl.formatMessage({ id: 'homepage.imgAlt.accenture' })}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 160 }}>
                    <img
                      src="/images/pwc-logo.svg"
                      style={{ width: '70%' }}
                      alt={intl.formatMessage({ id: 'homepage.imgAlt.pwc' })}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 160 }}>
                    <img
                      src="/images/oracle-logo.png"
                      style={{ width: '70%' }}
                      alt={intl.formatMessage({ id: 'homepage.imgAlt.oracle' })}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 160 }}>
                    <img
                      src="/images/auto-zone-logo.png"
                      style={{ width: '70%' }}
                      alt={intl.formatMessage({ id: 'homepage.imgAlt.autoZone' })}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 160 }}>
                    <img
                      src="/images/ar-state-gov-logo.jpg"
                      style={{ width: '70%' }}
                      alt={intl.formatMessage({ id: 'homepage.imgAlt.arStateCarousel' })}
                    />
                  </Box>
                </Box>
              </Slider>
            </Box>
          ) : (
            <Grid container spacing={0}>
              <Grid xs={2} pl={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/images/tyson-foods-logo.svg"
                  style={{ width: '50%' }}
                  alt={intl.formatMessage({ id: 'homepage.imgAlt.tysonFoods' })}
                />
              </Grid>
              <Grid xs={2} pl={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/images/accenture-logo.png"
                  style={{ width: '50%' }}
                  alt={intl.formatMessage({ id: 'homepage.imgAlt.accenture' })}
                />
              </Grid>
              <Grid xs={2} pl={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/images/pwc-logo.svg"
                  style={{ width: '50%' }}
                  alt={intl.formatMessage({ id: 'homepage.imgAlt.pwc' })}
                />
              </Grid>
              <Grid xs={2} pl={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/images/oracle-logo.png"
                  style={{ width: '50%' }}
                  alt={intl.formatMessage({ id: 'homepage.imgAlt.oracle' })}
                />
              </Grid>
              <Grid xs={2} pl={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/images/auto-zone-logo.png"
                  style={{ width: '50%' }}
                  alt={intl.formatMessage({ id: 'homepage.imgAlt.autoZone' })}
                />
              </Grid>
              <Grid xs={2} pl={2} sx={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src="/images/ar-state-gov-logo.jpg"
                  style={{ width: '50%' }}
                  alt={intl.formatMessage({ id: 'homepage.imgAlt.arStateCarousel' })}
                />
              </Grid>
            </Grid>
          )}
        </Container>
      </Box>
      {/* Three Items */}
      <Box sx={{ pt: { xs: 6, md: 10 }, pb: { xs: 6, md: 10 } }} style={{ backgroundColor: '#f9f9fa' }}>
        <Container maxWidth="lg" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Grid container sx={{ width: '100%' }} spacing={isMobile ? 4 : 5}>
            <Grid xs={12} md={4} mb={3}>
              <FeatureCard
                icon={<RouteIcon sx={{ fontSize: 45, color: 'primary.main' }} />}
                title={intl.formatMessage({ id: 'homepage.middle.p1Title' })}
                subtitle={intl.formatMessage({ id: 'homepage.middle.p1' }, { count: '400+' })}
                p={intl.formatMessage({ id: 'homepage.bottom.p1' })}
              />
            </Grid>
            <Grid xs={12} md={4} mb={3}>
              <FeatureCard
                icon={<HandshakeIcon sx={{ fontSize: 45, color: 'primary.main' }} />}
                title={intl.formatMessage({ id: 'homepage.middle.p2Title' })}
                subtitle={intl.formatMessage({ id: 'homepage.middle.p2' }, { count: '400+' })}
                p={intl.formatMessage({ id: 'homepage.bottom.p2' })}
              />
            </Grid>
            <Grid xs={12} md={4} mb={3}>
              <FeatureCard
                icon={<LocalLibraryIcon sx={{ fontSize: 45, color: 'primary.main' }} />}
                title={intl.formatMessage({ id: 'homepage.middle.p3Title' })}
                subtitle={intl.formatMessage({ id: 'homepage.middle.p3' }, { count: '400+' })}
                p={intl.formatMessage({ id: 'homepage.bottom.p3' })}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          padding: '40px 0 40px',
        }}
      >
        <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 6 }, pb: { xs: 2, md: 6 }, alignItems: 'center' }}>
          <Grid container spacing={3}>
            <Grid xs={12} md={5} sx={{ pt: { md: 3.5 }, display: 'flex', justifyContent: 'center' }}>
              <Box
                sx={{
                  maxWidth: { xs: '100%', md: 350, margin: { xs: 'auto', md: 'initial' } },
                }}
              >
                <CardMedia
                  component="img"
                  sx={{
                    maxWidth: { xs: '300px', md: '100%' },
                  }}
                  image="/images/women-hiw.png"
                  alt="Women"
                />
              </Box>
            </Grid>
            <Grid xs={12} md={7}>
              <Box>
                <Typography variant="h2" sx={{ fontSize: 32, mb: 3, fontWeight: 500 }}>
                  {intl.formatMessage({ id: 'homepage.benefits.title' })}
                </Typography>

                <Stack spacing={2} mb={3}>
                  <MechanicStep
                    icon={<LooksOneTwoToneIcon color="secondary" sx={{ fontSize: '1.5rem' }} />}
                    title={intl.formatMessage({ id: 'homepage.benefits.b1' })}
                  />
                  <MechanicStep
                    icon={<LooksTwoTwoToneIcon color="secondary" sx={{ fontSize: '1.5rem' }} />}
                    title={intl.formatMessage({ id: 'homepage.benefits.b2' })}
                  />
                  <MechanicStep
                    icon={<Looks3TwoToneIcon color="secondary" sx={{ fontSize: '1.5rem' }} />}
                    title={intl.formatMessage({ id: 'homepage.benefits.b3' })}
                  />
                  <MechanicStep
                    icon={<Looks4TwoToneIcon color="secondary" sx={{ fontSize: '1.5rem' }} />}
                    title={intl.formatMessage({ id: 'homepage.benefits.b4' })}
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    color="primary"
                    fullWidth={isMobile}
                    onClick={handleLogin}
                    sx={{ fontWeight: 700 }}
                    id="log-in-how-it-works-public-button"
                  >
                    {intl.formatMessage({ id: 'homepage.login' })}
                  </Button>

                  <Button
                    variant="contained"
                    size="large"
                    color="primary"
                    fullWidth={isMobile}
                    onClick={handleLogin}
                    sx={{ fontWeight: 700 }}
                    id="sign-up-how-it-works-public-button"
                  >
                    {intl.formatMessage({ id: 'homepage.signup' })}
                  </Button>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box sx={{ backgroundColor: '#f9f9fa' }}>
        <Container maxWidth="md" sx={{ pt: 7, pb: 10 }} className="slider-container">
          <Typography variant="h2" sx={{ fontSize: 32, mb: 4, fontWeight: 500, textAlign: 'left' }}>
            {intl.formatMessage({ id: 'homepage.additionalResources' })}
          </Typography>
          <Grid container spacing={4} mt={1}>
            <Grid xs={12} lg={6}>
              <Card elevation={3} sx={{ p: 3, boxShadow: `rgba(0, 0, 0, 0.3) 2px 2px 5px -1px` }}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  <DashboardOutlinedIcon fontSize="large" sx={{ fontSize: 45, color: 'secondary.dark' }} />
                  <Box>
                    <Typography variant="h3" fontWeight="bold" sx={{ mb: 1.5, color: 'tertiary.main' }}>
                      {intl.formatMessage({ id: 'homepage.additionalRecs1.title' })}
                    </Typography>

                    <Button
                      variant="outlined"
                      href={'https://myardashboard.arkansas.gov/'}
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<LaunchIcon />}
                      sx={{ borderColor: (theme) => theme.palette.primary.main }}
                      id="workforce-dashboard-public-button"
                    >
                      {intl.formatMessage({ id: 'homepage.additionalRecs.cta' })}
                    </Button>
                  </Box>
                </Box>
              </Card>
            </Grid>
            <Grid xs={12} lg={6}>
              <Card elevation={3} sx={{ p: 3, boxShadow: `rgba(0, 0, 0, 0.3) 2px 2px 5px -1px` }}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  <SupportIcon fontSize="large" sx={{ fontSize: 45, color: 'secondary.dark' }} />
                  <Box>
                    <Typography variant="h3" fontWeight="bold" sx={{ mb: 1.5, color: 'tertiary.main' }}>
                      {intl.formatMessage({ id: 'homepage.additionalRecs2.title' })}
                    </Typography>

                    <Button
                      variant="outlined"
                      href="https://myarciviform.arkansas.gov/programs"
                      target="_blank"
                      rel="noopener noreferrer"
                      endIcon={<LaunchIcon />}
                      sx={{ borderColor: (theme) => theme.palette.primary.main }}
                      id="support-services-public-button"
                    >
                      {intl.formatMessage({ id: 'homepage.additionalRecs.cta' })}
                    </Button>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
}

function MechanicStep(props) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {props.icon}
      <Typography variant="h6" component="p" fontWeight="regular">
        {props.title}
      </Typography>
    </Box>
  );
}
MechanicStep.propTypes = {
  title: PropTypes.string,
  icon: PropTypes.node,
};

function FeatureCard(props) {
  return (
    <Card elevation={3} sx={{ pt: 3, px: 3, pb: 0.5, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        {props.icon}
        <Typography variant="h3" fontWeight="bold" sx={{ color: 'primary.main' }}>
          {props.title}
        </Typography>
      </Box>

      <Typography variant="h6" component="p" fontWeight="regular">
        {props.p}
      </Typography>
    </Card>
  );
}

FeatureCard.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  p: PropTypes.string,
  icon: PropTypes.node,
};

export default LaunchHP;
